import { patientResponseSchema, type PatientResponse, type PatientSearchQuery } from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * The patient-demographics *read* surface, validated against `@emr/shared`'s own schemas —
 * matching `api/auth.ts`'s own reasoning in full: the server's real response shape, not a
 * locally-invented approximation of it.
 *
 * Neither endpoint wraps its response in a shared list-response schema
 * (`server/src/clinical/patientsRoutes.ts` — `{ patients: [...] }` and `{ patient: ... }`, composed
 * inline here rather than reusing a generic wrapper that does not exist), matching how `auth.ts`
 * composes `meResponseSchema` inline for its own single-field envelope.
 *
 * **No `updatePatient` here.** `PATCH /api/patients/:patientId` exists and is capability-gated
 * (`patient:write`), but the task brief that produced this page named it optional — "wire this only
 * if time allows; read-only chart is the priority" — and it was not reached. A demographics-edit
 * form is real, untested UI surface; leaving the function out is more honest than shipping one
 * nothing calls.
 */

const patientListResponseSchema = z.strictObject({ patients: z.array(patientResponseSchema) });
const patientDetailResponseSchema = z.strictObject({ patient: patientResponseSchema });

/**
 * `GET /api/patients` — search/list, requiring `patient:read`.
 *
 * `query` is `patientSearchQuerySchema`'s already-parsed shape (`mrn`/`name`/`firstName`/
 * `dateOfBirth`/`limit`), not raw form state — the caller is responsible for turning an empty
 * search field into `undefined` before this runs, since `patientSearchQuerySchema.parse` on
 * `{ mrn: '' }` would reject it (`trimmed(...)` requires at least one character).
 */
export async function searchPatients(
  query: Partial<PatientSearchQuery>,
  signal?: AbortSignal,
): Promise<readonly PatientResponse[]> {
  const params = new URLSearchParams();
  if (query.mrn !== undefined) params.set('mrn', query.mrn);
  if (query.name !== undefined) params.set('name', query.name);
  if (query.firstName !== undefined) params.set('firstName', query.firstName);
  if (query.dateOfBirth !== undefined) params.set('dateOfBirth', query.dateOfBirth);
  // Repeated key (`?ids=a&ids=b`), matching the server's own `c.req.queries('ids')` read
  // (`patientsRoutes.ts`) — `.set` would overwrite each prior id instead of accumulating them.
  if (query.ids !== undefined) {
    for (const id of query.ids) params.append('ids', id);
  }
  if (query.limit !== undefined) params.set('limit', String(query.limit));

  const qs = params.toString();
  const response = await apiRequest(
    qs === '' ? '/api/patients' : `/api/patients?${qs}`,
    patientListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.patients;
}

/** `GET /api/patients/:patientId` — single patient, requiring `patient:read` plus chart access. */
export async function getPatient(
  patientId: string,
  signal?: AbortSignal,
): Promise<PatientResponse> {
  const response = await apiRequest(
    `/api/patients/${patientId}`,
    patientDetailResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.patient;
}
